import { useNavigate } from 'react-router-dom';
import { ChevronRight, Lock, Trophy } from '../../../components/ui/icons';
import { useLeagueRounds } from '../../../hooks/useQueries';
import { useHaptics } from '../../../hooks/useHaptics';
import { cn } from '../../../lib/cn';
import { KNOCKOUT_STAGES, stageDate } from './constants';

export function KnockoutBracket({ leagueId }: { leagueId: string }) {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { data: rounds } = useLeagueRounds(leagueId);

  const knockout = (rounds ?? []).filter((r) => !/group/i.test(r.apiFootballRound));
  const published = knockout.length > 0;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 pb-3">
        <Trophy width={16} height={16} strokeWidth={2.4} className="text-ink2" />
        <span className="text-[15px] font-extrabold tracking-[-0.02em] text-ink">Knockout bracket</span>
      </div>

      <div className="flex flex-col gap-2">
        {KNOCKOUT_STAGES.map((s) => {
          const round = knockout.find((r) => s.match.test(r.apiFootballRound));
          return (
            <button
              key={s.label}
              type="button"
              disabled={!round}
              onClick={() => {
                if (!round) return;
                impact('light');
                navigate(`/play/${leagueId}?tab=fixtures&round=${round.roundId}`);
              }}
              className={cn(
                'tap flex items-center gap-3 rounded-[14px] border px-[14px] py-[12px] text-left',
                round ? 'border-black/8 bg-white' : 'border-black/6 bg-fill/40'
              )}
            >
              <div
                className={cn(
                  'grid h-7 w-7 place-items-center rounded-full',
                  round ? 'bg-accent/12' : 'bg-fill'
                )}
              >
                {round ? (
                  <Trophy width={13} height={13} strokeWidth={2.3} className="text-accent-ink" />
                ) : (
                  <Lock width={13} height={13} strokeWidth={2.3} className="text-ink3" />
                )}
              </div>
              <span className="flex-1 text-[13px] font-bold tracking-[-0.01em] text-ink">{s.label}</span>
              {round ? (
                <>
                  <span className="text-[12px] font-medium text-ink2">{stageDate(round)}</span>
                  <ChevronRight width={16} height={16} strokeWidth={2.4} className="text-ink3" />
                </>
              ) : (
                <span className="text-[11px] font-semibold text-ink3">To be decided</span>
              )}
            </button>
          );
        })}
      </div>

      {!published && (
        <p className="mt-3 px-1 text-[12px] font-medium leading-[1.45] text-ink2">
          The bracket locks once the group stage wraps — the 12 group winners and runners-up, plus
          the eight best third-placed teams, advance to the Round of 32.
        </p>
      )}
    </div>
  );
}
